import _extends from '@babel/runtime/helpers/extends';
import React__default from 'react';
import styled, { css } from 'styled-components';
import { F as Flex } from '../Flex/Flex-77mGg7gI.js';
import { f as focusOutline } from '../theme/style-mixins/FocusIndicator/focusOutline-oDdaDZ4N.js';
import { i as isMobile } from '../theme/style-mixins/isMobile/isMobile-CGsYoba7.js';
import { s as spinner } from '../LocalModules/storm-ui-icons/dist/esm/localIcons-9_jhfe7X-DyCR5FH7.js';
import { C as CloseButton } from '../Button/CloseButton/CloseButton-B_qumqxY.js';
import { u as useTaktId } from '../TaktIdContext/TaktIdContext-C6vdL-6Z.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-B0CzqlN8.js';
import { I as Icon } from '../Icon/Icon-CoM8rI0H.js';
import { T as Text } from '../Text/Text-CO4HftGt.js';

const isSmallSize = size => size === 'small';
const borderRadius = ({
  theme
}) => theme.border.radius.medium;
const bgColor = ({
  $loading,
  $disabled,
  $applied,
  theme
}) => {
  if ($loading || $disabled && $applied) {
    return theme.selectableContainer.backgroundColor.selected;
  }
  return theme.selectableContainer.backgroundColor.default;
};
const textColor = ({
  $loading,
  $disabled,
  theme
}) => {
  if ($loading || $disabled) return theme.text.color.unavailable;
  return theme.text.color.default;
};
const desktopHeight = ({
  $size,
  theme
}) => isSmallSize($size) ? theme.controls.height.small.default : theme.controls.height.base.default;
const mobileHeight = ({
  $size,
  theme
}) => isSmallSize($size) ? theme.controls.height.small.mobile : theme.controls.height.small.default;
const selectWidth = ({
  fullWidth,
  maxWidth
}) => {
  if (fullWidth) {
    /**
     * there is an issue with flex children shrinking beyond their content.
     * to fix this, when `fullWidth` is set, we set a "min-width: 0" on flexed child elements
     */
    return 'min-width: 0; width: 100%';
  }
  if (typeof maxWidth === 'number') {
    return `max-width: ${maxWidth}px`;
  }
  if (typeof maxWidth === 'string') {
    return `max-width: ${maxWidth}`;
  }
  return '';
};
const LabelContainer = /*#__PURE__*/styled(Flex).withConfig({
  componentId: "sc-storm-ui-40026394__sc-minkvh-0"
})(["", ";"], selectWidth);
const PillFrame = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40026394__sc-minkvh-1"
})(["box-sizing:border-box;display:inline-flex;flex-direction:row;flex-wrap:nowrap;justify-content:flex-start;align-content:stretch;align-items:stretch;min-height:", ";background-color:", ";isolation:isolate;border:", ";", " ", " ", " ", " border-radius:", ";> *{color:", ";", " background-color:transparent;border:none;margin:0;padding-block:", ";padding-inline:", ";", "}> *:first-child{border-start-start-radius:", ";border-end-start-radius:", ";}> *:last-child{border-start-end-radius:", ";border-end-end-radius:", ";}"], desktopHeight, bgColor, ({
  theme,
  $disabled,
  $applied
}) => {
  if ($disabled) {
    return theme.selectableContainer.border.unavailable;
  }
  if ($applied) {
    return theme.selectableContainer.border.selected;
  }
  return theme.selectableContainer.border.default;
}, ({
  theme,
  $size
}) => isSmallSize($size) && `border-width: ${theme.border.width.default};`, ({
  $fullWidth
}) => $fullWidth && css(["width:100%;"]), ({
  theme,
  $loading,
  $disabled,
  $applied,
  $isClickable,
  $isCloseable
}) => !$loading && !$disabled && ($isClickable || $isCloseable) && css([":hover{border-color:", ";background-color:", ";}"], $applied ? theme.selectableContainer.borderColor.selectedHover : theme.selectableContainer.borderColor.hover, theme.selectableContainer.backgroundColor.hover), isMobile(css(["min-height:", ";"], mobileHeight)), borderRadius, textColor, ({
  $isClickable
}) => $isClickable && 'cursor: pointer;', ({
  theme,
  $size
}) => isSmallSize($size) ? theme.controls.paddingBlock.xsmall.default : theme.controls.paddingBlock.base2pxBorder.default, ({
  theme
}) => theme.controls.paddingInline.base.default, isMobile(css(["padding-block:", ";padding-inline:", ";"], ({
  theme,
  $size
}) => isSmallSize($size) ? theme.controls.paddingBlock.xsmall.mobile : theme.controls.paddingBlock.base2pxBorder.mobile, ({
  theme
}) => theme.controls.paddingInline.base.mobile)), borderRadius, borderRadius, borderRadius, borderRadius);
PillFrame.displayName = 'PillFrame';
/**
 * there is an issue with flex children shrinking beyond their content.
 * to fix this, when `fullWidth` is set, we set a "min-width: 0" on flexed child elements
 */
const contentMixin = /*#__PURE__*/css(["flex:1 1 auto;", " display:flex;flex-direction:row;flex-wrap:nowrap;justify-content:center;align-content:center;align-items:center;"], ({
  fullWidth
}) => fullWidth && css(["min-width:0;width:100%;"]));
const NonClickableContent = /*#__PURE__*/styled('div').withConfig({
  componentId: "sc-storm-ui-40026394__sc-minkvh-2"
})(["", ""], contentMixin);
const ClickableContent = /*#__PURE__*/styled('button').withConfig({
  componentId: "sc-storm-ui-40026394__sc-minkvh-3"
})(["", " font:unset;outline:none;overflow:hidden;cursor:", ";:focus{", " z-index:1;}"], contentMixin, ({
  disabled,
  $loading,
  onClick
}) => {
  if (disabled) return 'not-allowed';
  if ($loading) return 'not-allowed';
  return onClick ? 'pointer' : 'default';
}, focusOutline);
const Content = ({
  disabled = false,
  title,
  fullWidth,
  onClick,
  applied = true,
  taktId,
  taktValue,
  loading = false,
  ...rest
}) => {
  const {
    getDataTaktAttributes
  } = useTaktId({
    taktId,
    fallbackId: createStormTaktId('pill')
  });
  if (onClick) {
    return /*#__PURE__*/React__default.createElement(ClickableContent, _extends({
      type: "button"
    }, getDataTaktAttributes({
      taktValue
    }), rest, {
      disabled: disabled,
      fullWidth: fullWidth,
      title: title,
      onClick: loading ? undefined : onClick,
      "aria-pressed": applied,
      $loading: loading,
      tabIndex: disabled || loading ? -1 : 0
    }, loading && {
      'aria-live': 'assertive'
    }, loading && {
      'aria-disabled': true
    }));
  }
  return /*#__PURE__*/React__default.createElement(NonClickableContent, _extends({}, rest, {
    disabled: disabled,
    fullWidth: fullWidth,
    title: title
  }, loading && {
    'aria-live': 'assertive'
  }));
};
const StyledIcon = /*#__PURE__*/styled(Icon).withConfig({
  componentId: "sc-storm-ui-40026394__sc-minkvh-4"
})(["padding-inline-end:", ";", ""], ({
  theme
}) => theme.controls.paddingInline.base.default, isMobile(css(["padding-inline-end:", ";"], ({
  theme
}) => theme.controls.paddingInline.base.mobile)));
const textMixin = /*#__PURE__*/css(["overflow:hidden;text-overflow:ellipsis;white-space:nowrap;"]);
const fixTextMixin = /*#__PURE__*/css(["font-weight:bold;"]);
const LabelText = /*#__PURE__*/styled(Text).withConfig({
  componentId: "sc-storm-ui-40026394__sc-minkvh-5"
})(["", " ", " ", ""], textMixin, ({
  $withPrefixMargin
}) => $withPrefixMargin && css(["margin-inline-start:", ";", ""], ({
  theme
}) => theme.controls.paddingInline.base.default, isMobile(css(["margin-inline-start:", ";"], ({
  theme
}) => theme.controls.paddingInline.base.mobile))), ({
  $withSuffixMargin
}) => $withSuffixMargin && css(["margin-inline-end:", ";", " "], ({
  theme
}) => theme.controls.paddingInline.base.default, isMobile(css(["margin-inline-end:", ";"], ({
  theme
}) => theme.controls.paddingInline.base.mobile))));
const selectFixTextMaxWidth = ({
  $fullWidth,
  $maxWidth,
  $hasPrefixAndSuffix
}) => {
  const divisor = $hasPrefixAndSuffix ? 0.3 : 0.5;
  if ($fullWidth) {
    return `max-width: calc(100% * ${divisor});`;
  }
  if (typeof $maxWidth === 'number') {
    return `max-width: calc(${$maxWidth}px * ${divisor});`;
  }
  if (typeof $maxWidth === 'string') {
    return `max-width: calc(${$maxWidth} * ${divisor});`;
  }
  return '';
};
const FixText = /*#__PURE__*/styled(Text).withConfig({
  componentId: "sc-storm-ui-40026394__sc-minkvh-6"
})(["", " ", " min-width:", ";", " ", ""], textMixin, selectFixTextMaxWidth, ({
  theme
}) => theme.spacing.base.default, isMobile(css(["min-width:", ";"], ({
  theme
}) => theme.spacing.base.mobile)), fixTextMixin);
const PrefixColon = /*#__PURE__*/styled(Text).withConfig({
  componentId: "sc-storm-ui-40026394__sc-minkvh-7"
})(["", ""], fixTextMixin);
const getTitle = ({
  label,
  prefix,
  suffix
}) => {
  let title = '';
  if (prefix) {
    title += prefix;
    if (label) {
      title += ': ';
    }
  }
  if (label) {
    title += label;
  }
  if (suffix) {
    title += title ? ` ${suffix}` : suffix;
  }
  return title;
};
const Pill = ({
  label,
  prefix,
  suffix,
  size = 'base',
  applied = false,
  disabled = false,
  fullWidth = false,
  maxWidth = 500,
  className = '',
  onClick,
  onClose,
  closeLabel = 'remove',
  tabIndex,
  taktId,
  taktValue,
  loading = false,
  loadingLabel,
  iconStart,
  ...rest
}) => /*#__PURE__*/React__default.createElement(PillFrame, _extends({}, rest, {
  $size: size,
  className: className,
  $disabled: disabled,
  $applied: applied,
  $isClickable: typeof onClick === 'function',
  $isCloseable: typeof onClose === 'function',
  $loading: loading,
  $fullWidth: fullWidth
}), /*#__PURE__*/React__default.createElement(Content, {
  taktId: taktId,
  taktValue: taktValue,
  disabled: disabled,
  title: getTitle({
    label,
    prefix,
    suffix
  }),
  fullWidth: fullWidth,
  onClick: onClick,
  loading: loading
}, loading ? /*#__PURE__*/React__default.createElement(StyledIcon, {
  type: spinner
}) : /*#__PURE__*/React__default.createElement(React__default.Fragment, null, iconStart && /*#__PURE__*/React__default.createElement(StyledIcon, {
  type: iconStart
})), /*#__PURE__*/React__default.createElement(LabelContainer, {
  maxWidth: maxWidth,
  fullWidth: fullWidth,
  alignItems: "center",
  alignContent: "center",
  direction: "row",
  justifyContent: "center",
  wrap: "nowrap",
  grow: 1,
  shrink: 1,
  basis: "auto"
}, loading && loadingLabel ? /*#__PURE__*/React__default.createElement(LabelText, {
  renderAs: "span",
  color: "inherit"
}, loadingLabel) : /*#__PURE__*/React__default.createElement(React__default.Fragment, null, prefix && /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(FixText, {
  renderAs: "span",
  color: "inherit",
  $fullWidth: fullWidth,
  $maxWidth: maxWidth,
  $hasPrefixAndSuffix: Boolean(suffix)
}, prefix), label && /*#__PURE__*/React__default.createElement(PrefixColon, {
  renderAs: "span",
  color: "inherit"
}, ":")), /*#__PURE__*/React__default.createElement(LabelText, {
  renderAs: "span",
  color: "inherit",
  $withPrefixMargin: Boolean(prefix),
  $withSuffixMargin: Boolean(suffix)
}, label), suffix && /*#__PURE__*/React__default.createElement(FixText, {
  renderAs: "span",
  color: "inherit",
  $fullWidth: fullWidth,
  $maxWidth: maxWidth,
  $hasPrefixAndSuffix: Boolean(prefix)
}, suffix)))), typeof onClose === 'function' && /*#__PURE__*/React__default.createElement(CloseButton, {
  disabled: loading || disabled,
  onClick: onClose,
  tabIndex: loading || disabled ? -1 : 0,
  size: size,
  label: closeLabel
}));

export { Pill as P };
//# sourceMappingURL=Pill-BvZ0Kj__.js.map
